import { assign, fromPromise, sendTo, setup } from 'xstate';
import { Garage } from '../../shared/models/garage';

export interface SignalRHubMachineOptions {
    startConnection: () => Promise<void>;
    syncGroups: (selected: Garage[], subscribed: Garage[]) => Promise<void>;
    onConnected: (garagesToPublish: Garage[]) => Promise<void>;
    onConnectedError: (error?: unknown) => void;
    onReconnecting: (error?: unknown) => void;
    onReconnected: () => void;
    onClearStaleWarnings: () => void;
    onDisconnected: (error?: unknown) => void;
    onGroupsChanged: (groups: Garage[]) => void;
    onSyncError: (error?: unknown) => void;
}

interface SignalRHubContext {
    selectedGarages: Garage[];
    subscribedGarages: Garage[];
    garagesToPublish: Garage[];
    retryCount: number;
    lastError?: unknown;
}

type SignalRHubEvent =
    | { type: 'SELECTED_GARAGES_UPDATED'; garages: Garage[] }
    | { type: 'RECONNECTING'; error?: unknown }
    | { type: 'RECONNECTED' }
    | { type: 'CLEAR_STALE_WARNINGS' }
    | { type: 'CONNECTION_CLOSED'; error?: unknown }
    | { type: 'RETRY' };

export const createSignalRHubMachine = (options: SignalRHubMachineOptions) => {
    return setup({
        types: {
            context: {} as SignalRHubContext,
            events: {} as SignalRHubEvent,
            input: {} as undefined,
        },
        actors: {
            startConnection: fromPromise(async () => {
                await options.startConnection();
            }),
            syncGroups: fromPromise(
                async ({
                    input,
                }: {
                    input: { selected: Garage[]; subscribed: Garage[] };
                }) => {
                    await options.syncGroups(input.selected, input.subscribed);
                    return input.selected;
                },
            ),
            onConnected: fromPromise(
                async ({ input }: { input: { garagesToPublish: Garage[] } }) => {
                    await options.onConnected(input.garagesToPublish);
                },
            ),
        },
        guards: {
            needsSync: ({ context }) => {
                const selected = context.selectedGarages || [];
                const subscribed = context.subscribedGarages || [];
                return selected.length !== subscribed.length ||
                       !selected.every(s => subscribed.some(sub => sub.id === s.id));
            },
        },
    }).createMachine({
        /** @xstate-layout N4IgpgJg5mDOIC5SwJZQHYEMA2AnAtABYCuARgMQDKAogDLUDCAKtQCID6A4gIIBK3napXYBVAAqtuLVgG0ADAF1EoAA4B7VABcUa9MpAAPRADYAjMYB0cgBwB2AKxzbxgCwAmFy4DMpgDQgAT0RTNwBfUP9UDBwCElILAGNddDAE7XQocghdMAsUdAA3NQBrXKisPCIyROTU9KgEfKKEzG1deQUO-XUtHT0kQ0QvNzcLawBOSfG3YxtjH2N-IIQvcbkLU1trUxc5N3HTa3t7O3DItArY6qT0FLT8zLBcXDVcCxVsVoAzV4BbC3KMSq8Rud3qjUKahabXQHS6Ax6KBh+iMKxGYym01m1nm5iWiH24w2LmM1jJkzk4xJ9jOIEBlTiFlwYE0uACD3IBlgmlauUwX00TwAFKY5GKAJTkelXeLM1nsjLw1QaJF9FHBEZeCz2Yxuewkhx7Lz2fEIQ7rWx6uSi0z2A4uca06XAmq3OqQci8RgAeQAcr7GEwAJK+zhKkCI5EDVGW0bGcZeLY7SlHOym-UuCwzXWk9zWFz2fZOi5Axmg90QcgMP0B5hBv3sBi0b00WSKboqqOgGPo+OJ7a7cap2ymgvrNzmPXjYz2HamabF6IM661NIe8ORtXRoYOrPeWzWg92EL60fbLOU1a26bOQuLy4u5nl+4ZT0+-2Btgbztb7uIWxbBsCa2F4upuM4dheKaWyjJSpIJl41hyH2xj3qW1RPqu9RVjWgb1r6jbNq2369Lo6pmqYIQWC4dj2AeE6iqm0HWLB04TF4iHIQmqERHSJbLvEsABOgCQctkKR5JCpQAvxMoAsJokZBCzStH0cLtgiP5kduaKjBMUwzHMCzpnq2q2IcCY0fOZg0rxzqMkJIkck8LxvB83x-DJS5yY5ikNE0UKqe0igkaq2l-rpGIGdiuKLIEJjuNqayuCl1heNaYR2bJLq+RyXrVh+dahqFXaDJF+mTIZOLGfFCDTJmuoWQB8YTnItnnN5OUKRyBW1sGDZNi2X4acqpH9BFwx6ZiVWxaaNrWBYxjmcl+q6p4LjhLx6BqBAcD6PZZAdmN5H4H4tUJlYjhinYAFyBxWxoQJrpgg8R1heNZUuCOtWgZmB66tMcFUtaj1yXKbKvZpx06aYxpal43gsXslKUgWo4OFYd0ARmaxYxtWWdWWWGQG9pWojqWoOGthnGK4d0mQt8YhJ4qyprOoOPqkWGQ6N73keM9hascYo+LsWxLS4zEWmxzgFvqIwcw53UZKTv5lSBVjbBOs4TDsQ5uKa05ak4Bw2Eh+s8eEQA */
        id: 'signalr-hub',
        initial: 'connecting',
        context: {
            selectedGarages: [],
            subscribedGarages: [],
            garagesToPublish: [],
            retryCount: 0,
        },
        on: {
            SELECTED_GARAGES_UPDATED: {
                actions: assign({
                    selectedGarages: ({ event }) => event.garages,
                    garagesToPublish: ({ context, event }) =>
                        event.garages.filter(
                            (garage) =>
                                !context.subscribedGarages.some(
                                    (subscribed) => subscribed.id === garage.id,
                                ),
                        ),
                }),
            },
            CLEAR_STALE_WARNINGS: {
                actions: () => options.onClearStaleWarnings(),
            },
        },
        states: {
            connecting: {
                invoke: {
                    src: 'startConnection',
                    onDone: {
                        target: 'connected',
                        actions: assign({
                            retryCount: () => 0,
                            lastError: () => undefined,
                        }),
                    },
                    onError: {
                        target: 'retrying',
                        actions: assign({
                            retryCount: ({ context }) => context.retryCount + 1,
                            lastError: ({ event }) => event.error,
                        }),
                    },
                },
            },
            retrying: {
                after: {
                    1000: {
                        target: 'connecting',
                    },
                },
            },
            connected: {
                invoke: {
                    src: 'onConnected',
                    input: ({ context }) => ({
                        garagesToPublish: context.garagesToPublish,
                    }),
                    onDone: {
                        actions: assign({
                            garagesToPublish: () => [],
                        }),
                    },
                    onError: {
                        actions: ({ event }) =>
                            options.onConnectedError(event.error),   
                    },
                },
                always: {
                    guard: 'needsSync',
                    target: 'syncing',
                },
                on: {
                    RECONNECTING: {
                        target: 'reconnecting',
                    },
                    CONNECTION_CLOSED: {
                        target: 'retrying',
                        actions: ({ event }) =>
                            options.onDisconnected(event.error),
                    },
                },
            },
            reconnecting: {
                entry: ({ event }) => {
                    if (event.type === 'RECONNECTING') {
                        options.onReconnecting(event.error);
                    }
                },
                on: {
                    RECONNECTED: {
                        target: 'syncing',
                        actions: [
                            () => options.onReconnected(),
                            assign({
                                subscribedGarages: () => [],
                                garagesToPublish: ({ context }) =>
                                    context.selectedGarages,
                            }),
                            sendTo(
                                ({ self }) => self,
                                { type: 'CLEAR_STALE_WARNINGS' },
                                { delay: 15000 }
                            ),
                        ],
                    },
                    CONNECTION_CLOSED: {
                        target: 'retrying',
                        actions: ({ event }) =>
                            options.onDisconnected(event.error),
                    },
                },
            },
            syncing: {
                invoke: {
                    src: 'syncGroups',
                    input: ({ context }) => ({
                        selected: context.selectedGarages,
                        subscribed: context.subscribedGarages,
                    }),
                    onDone: {
                        target: 'connected',
                        actions: [
                            assign({
                                subscribedGarages: ({ event }) => event.output,
                            }),
                            ({ event }) =>
                                options.onGroupsChanged(event.output),
                        ],
                    },
                    onError: {
                        target: 'connected',
                        actions: ({ event }) =>
                            options.onSyncError(event.error),
                    },
                },
                on: {
                    RECONNECTING: {
                        target: 'reconnecting',
                    },
                    CONNECTION_CLOSED: {
                        target: 'retrying',
                        actions: ({ event }) =>
                            options.onDisconnected(event.error),
                    },
                },
            },
        },
    });
};
